import { GroupId } from "@custom-types/base-types";
import { ProjectArchivingState } from "@custom-types/project-types";
import { useAppDispatch } from "@store/store-helper";
import { resetTableState, setTableType } from "@store/table/table-slice";
import { TableType } from "@utils/track-event/track-event-list";
import { useEffect, useMemo } from "react";

/** Context in which the projects table is being displayed. Used by the bulk actions logic */
export type ProjectTableContextType =
  | "active projects"
  | "archived projects"
  | "group projects";

interface Props {
  /** The archiving status of the project */
  projectArchivingState: ProjectArchivingState;

  /** Id of the group of the projects (if the projects table is in the context of a group) */
  groupId?: GroupId;
}

/** Handles computing the table context type and setting it in the redux store */
export function useProjectsTableContext({
  projectArchivingState,
  groupId,
}: Props): ProjectTableContextType {
  const dispatch = useAppDispatch();

  // The current context in which the projects table is being used
  const contextType: ProjectTableContextType = useMemo(() => {
    if (groupId) {
      return "group projects";
    }
    if (projectArchivingState === ProjectArchivingState.active) {
      return "active projects";
    }
    return "archived projects";
  }, [groupId, projectArchivingState]);

 // As ProjectsTable is a component that is used in multiple places,
  // we need to make sure that the state is reset after contextType is changed
  useEffect(() => {
    dispatch(resetTableState());
    switch (contextType) {
      case "active projects":
        dispatch(setTableType(TableType.activeProjects));
        break;

      case "archived projects":
        dispatch(setTableType(TableType.archiveProjects));
        break;

      case "group projects":
        dispatch(setTableType(TableType.groupProjects));
        break;
    }

    return () => {
      dispatch(resetTableState());
    };
  }, [contextType, dispatch]);

  return contextType;
}
